<template>
  <div class="tw-flex tw-flex-col tw-gap-2 tw-rounded-lg">
    <div class="tw-flex tw-flex-col">
      <c-button
        v-if="chats.length > SHOW_DEFAULT_CHAT_AMOUNT"
        :icon="
          !showAllChats
            ? 'fa-duotone fa-message-arrow-up'
            : 'fa-duotone fa-message-arrow-down'
        "
        outline
        size="md"
        :label="
          !showAllChats
            ? `Toon alle chats (${props.chats?.length || 0})`
            : 'Toon minder chats'
        "
        class="tw-mb-2 tw-self-center"
        @click="showAllChats = !showAllChats"
      />

      <ul v-auto-animate class="c-chat-screen">
        <li v-if="!formattedChats.length">
          <span
            class="tw-flex tw-px-4 tw-py-2 tw-text-sm tw-italic tw-text-slate-500"
          >
            Er zijn nog geen reacties...</span
          >
        </li>

        <li
          v-for="chat in formattedChats"
          :key="chat.id ?? `${chat.name}-${chat.stamp}-${chat.text}`"
        >
          <c-chat-message :chat @edit="startEdit(chat)" />
        </li>
      </ul>
    </div>

    <c-form
      v-if="!readonly"
      ref="chatInputForm"
      class="tw-flex tw-gap-x-2"
      @submit="addChat"
    >
      <c-input
        v-model="message"
        :autofocus
        class="tw-flex-1"
        dense
        bg-color="white"
        :disable="sending"
        :placeholder
        :rules="[required]"
        lazy-rules="ondemand"
        hide-bottom-space
        @keydown.esc="cancelEdit"
      >
        <template #append>
          <c-button-icon
            v-if="editingChatId !== null"
            flat
            size="sm"
            icon="fa-solid fa-xmark"
            color="dark"
            class="tw-mr-1"
            :aria-label="t('shared.chat.cancelEdit')"
            @click="cancelEdit"
          />
          <q-icon
            :name="toClassicIcon(chatSendIcon, 'solid')"
            size="16px"
            color="dark"
            class="tw-cursor-pointer tw-transition-opacity hover:tw-opacity-70"
            @click="submitChatInputForm"
          />
        </template>
      </c-input>
    </c-form>

    <notification-message
      class="tw-my-4"
      :notification-key="notificationMessageContainer"
    />
  </div>
</template>

<script setup>
import { chatSendIcon, toClassicIcon } from "common/defaultIcons";
import { required } from "common/validationRules.js";
import NotificationMessage from "shared/components/NotificationMessage.vue";
import { useAuthStore } from "core/auth/auth.store";
import { useNotifyStore } from "store/modules/notifyStore.js";
import { storeToRefs } from "pinia";
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

// Notify store
const notifyStore = useNotifyStore();
const { setNotification } = notifyStore;

// Auth store — used to detect whether a chat was sent by the current user
const authStore = useAuthStore();
const { user: currentUser } = storeToRefs(authStore);

const props = defineProps({
  chats: {
    type: Array,
    default: () => [],
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  callbackFunction: {
    type: Function,
    default: () => {},
  },
  /**
   * Receives `{ ...extraParameters, chatId, message }`. Without it, reactions can't be edited.
   */
  editCallbackFunction: {
    type: Function,
    default: null,
  },
  extraParameters: {
    type: Object,
    default: () => ({}),
  },
  placeholder: {
    type: String,
    required: false,
    default: "Plaats een reactie...",
  },
  /**
   * Turn off wherever the chat sits under something the user came to fill in: focusing an
   * input on mount also scrolls it into view, so the screen opens on the reactieveld rather
   * than on the form above it.
   */
  autofocus: {
    type: Boolean,
    default: true,
  },
});

// Refs
const chatInputFormRef = useTemplateRef("chatInputForm");
const submitChatInputForm = () => chatInputFormRef.value?.formRef.submit();

const notificationMessageContainer = computed(() => {
  return Math.random().toString(36).substring(2, 15);
});

const generateChatTimestamp = (isoDate) => {
  const dateObj = new Date(isoDate);
  const dateOptions = {
    weekday: "short",
    day: "2-digit",
    month: "short",
  };
  const formattedDate = new Intl.DateTimeFormat("nl-NL", dateOptions).format(
    dateObj,
  );

  const timeOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };
  const formattedTime = new Intl.DateTimeFormat("nl-NL", timeOptions).format(
    dateObj,
  );

  return `${formattedDate} om ${formattedTime}`;
};

const SHOW_DEFAULT_CHAT_AMOUNT = 3;
const showAllChats = ref(false);
// GLOBAL-REACTION-EDITABLE-UNTIL-FOLLOW-UP-001: only the latest reaction can still be
// edited, and system messages don't count as a reply.
const lastUserChatId = computed(
  () => props.chats.findLast((chat) => chat.systemMessage !== true)?.id ?? null,
);

const mapChats = ({
  id,
  user_display_name,
  message,
  created_at,
  edited_at = null,
  user_id,
  isSender = null,
  systemMessage = false,
  user,
}) => {
  const sent = isSender ?? currentUser.value?.id === user_id;

  return {
    id,
    name: user_display_name,
    text: [message],
    stamp: edited_at
      ? t("shared.chat.editedAt", { date: generateChatTimestamp(edited_at) })
      : generateChatTimestamp(created_at),
    sent,
    editable:
      sent &&
      props.readonly === false &&
      props.editCallbackFunction !== null &&
      id !== undefined &&
      id === lastUserChatId.value,
    avatar: "/images/empty-profile-pic.webp",
    systemMessage,
    user,
  };
};

const formattedChats = computed(() => {
  const { chats = [] } = props || {};

  if (!chats.length) {
    return [];
  }

  const showAmount = showAllChats.value
    ? props.chats.length
    : SHOW_DEFAULT_CHAT_AMOUNT;

  return props.chats.map(mapChats).slice(-showAmount).reverse();
});

const sending = ref(false);
const message = ref("");
const editingChatId = ref(null);

const originalMessage = ref("");

const startEdit = (chat) => {
  editingChatId.value = chat.id;
  originalMessage.value = chat.text[0];
  message.value = chat.text[0];
};

const cancelEdit = () => {
  // Esc also lands here: outside edit mode the input holds a draft, keep it.
  if (editingChatId.value === null) {
    return;
  }

  editingChatId.value = null;
  message.value = "";
};

const editChat = async () => {
  // Unchanged text is not an edit: no request, no "Bewerkt op".
  if (message.value === originalMessage.value) {
    cancelEdit();
    return;
  }

  try {
    sending.value = true;

    await props.editCallbackFunction({
      ...props.extraParameters,
      chatId: editingChatId.value,
      message: message.value,
    });

    cancelEdit();

    setNotification({
      key: notificationMessageContainer.value,
      message: t("shared.chat.edited"),
      type: "success",
    });
  } catch (error) {
    console.error(error);

    // 403: a reply came in first, or access was lost. Retrying can't succeed, so leave edit mode.
    const refused = error?.response?.status === 403;
    if (refused) {
      cancelEdit();
    }

    setNotification({
      key: notificationMessageContainer.value,
      message: refused
        ? t("shared.chat.editRefused")
        : error?.response?.data?.message || t("shared.chat.editError"),
      type: "error",
    });
  } finally {
    sending.value = false;
  }
};

const addChat = async () => {
  if (props.readonly || sending.value) {
    return;
  }

  if (editingChatId.value !== null) {
    await editChat();
    return;
  }

  try {
    sending.value = true;

    const chatData = {
      ...props.extraParameters,
      message: message.value,
    };

    await props.callbackFunction(chatData);

    message.value = "";

    setNotification({
      key: notificationMessageContainer.value,
      message: "Reactie geplaatst",
      type: "success",
    });
  } catch (error) {
    console.error(error);

    setNotification({
      key: notificationMessageContainer.value,
      message:
        error?.response?.data?.message ||
        "Er is iets fout gegaan bij het plaatsen van je reactie, probeer het nog eens",
      type: "error",
    });
  } finally {
    sending.value = false;
  }
};
</script>

<style>
.q-message-text--sent {
  @apply tw-bg-blue-100;

  &::before,
  &:last-child::before {
    @apply tw-border-b-blue-100;
  }
}

.q-message-stamp {
  font-size: 0.8rem;
  color: #525252;
  text-align: right;
}

.c-chat-screen {
  @apply tw-flex tw-flex-col-reverse tw-gap-2 tw-overflow-hidden tw-px-0 tw-py-2;
}
</style>
